import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Terms and Conditions content
const USER_TERMS = `
TERMS AND CONDITIONS FOR BUYERS/SELLERS

Welcome to X7 Autoz! By creating an account, you agree to the following terms:

1. ACCOUNT REGISTRATION
- You must provide accurate and complete information during registration
- You are responsible for maintaining the confidentiality of your account
- You must be at least 18 years old to use this platform

2. BUYING VEHICLES
- All vehicles listed on X7 Autoz are from third-party sellers
- X7 Autoz does not guarantee the condition, accuracy, or availability of any vehicle
- Buyers should conduct their own due diligence before purchasing

3. SELLING VEHICLES
- Sellers must provide accurate and truthful information about their vehicles
- Sellers are responsible for ensuring the vehicle has clear title and no legal issues
- Listing fees are non-refundable once the vehicle is approved

4. PAYMENTS
- All payments are processed securely through our platform
- Listing fees must be paid for vehicle listings to be published
- GST and other applicable taxes will be added to the listing fee

5. PROHIBITED ACTIVITIES
- Fraudulent listings or misrepresentation
- Harassment of other users
- Any illegal activities

6. LIMITATION OF LIABILITY
- X7 Autoz is a marketplace platform and does not take responsibility for transactions between buyers and sellers
- Users engage in transactions at their own risk

7. PRIVACY
- Your personal information is protected as per our Privacy Policy
- We may use your contact information to send service-related communications

By checking the box, you acknowledge that you have read, understood, and agree to these Terms and Conditions.

Last updated: January 2025
`;

const AGENT_TERMS = `
TERMS AND CONDITIONS FOR AGENTS

Welcome to X7 Autoz Agent Program! By registering as an agent, you agree to the following terms:

1. AGENT REGISTRATION REQUIREMENTS
- Valid business registration documents
- PAN Card (Business and Personal)
- Aadhar Card for identity verification
- All documents will be verified by our team

2. AGENT RESPONSIBILITIES
- Maintain accurate and up-to-date vehicle listings
- Respond to buyer inquiries promptly
- Ensure all vehicles listed have proper documentation
- Provide honest and transparent information to buyers

3. COMMISSION AND FEES
- Agent listing fees may differ from regular user fees
- Commission structure will be communicated upon approval
- All fees are subject to GST and applicable taxes

4. VEHICLE VERIFICATION
- Agents must verify vehicle details before listing
- Misrepresentation may lead to account suspension
- Regular audits may be conducted on agent listings

5. RATING SYSTEM
- Agents will be rated by buyers after successful transactions
- Maintaining a good rating is essential for continued partnership
- Poor ratings may affect your visibility on the platform

6. CODE OF CONDUCT
- Professional behavior with all users
- No price manipulation or unfair practices
- Compliance with all local laws and regulations

7. TERMINATION
- X7 Autoz reserves the right to terminate agent accounts for violations
- Outstanding payments will be settled upon termination
- Agent must return any promotional materials upon termination

8. CONFIDENTIALITY
- Agent must maintain confidentiality of business information
- Customer data must not be shared with third parties

By checking the box, you acknowledge that you have read, understood, and agree to these Agent Terms and Conditions.

Last updated: January 2025
`;

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: 'user',
    city: '',
    agent_documents: null
  });
  const [agentDocs, setAgentDocs] = useState({
    business_registration: '',
    pan_card: '',
    aadhar_card: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchCities();
  }, []);

  const emailRef = useRef(null);
  
    useEffect(() => {
      emailRef.current.focus();
    }, []);

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/cities`);
      setCities(res.data.cities);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  // ✅ VALIDATION FUNCTION
  const validateForm = () => {
    const { email, phone, password, name, city } = formData;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email');
      return false;
    }

    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(phone)) {
      toast.error('Phone number must be exactly 10 digits');
      return false;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }

    if (!name.trim()) {
      toast.error('Name is required');
      return false;
    }

    if (!city) {
      toast.error('Please select a city');
      return false;
    }

    if (formData.role === 'agent') {
      if (
        !agentDocs.business_registration ||
        !agentDocs.pan_card ||
        !agentDocs.aadhar_card
      ) {
        toast.error('Please upload all required agent documents');
        return false;
      }
    }

    return true;
  };

  const handleFileUpload = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAgentDocs({ ...agentDocs, [field]: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    
    if (!termsAccepted) {
      toast.error('Please accept the Terms and Conditions to continue');
      return;
    }
    
    setLoading(true);
    try {
      const payload = { ...formData };
      if (formData.role === 'agent') {
        payload.agent_documents = agentDocs;
      }
      await register(payload);
      toast.success('Registration successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
  <div className="min-h-screen bg-gray-100 px-4 py-10">

    {/* 🔙 Back Button OUTSIDE CARD */}
    <div className="max-w-2xl mx-auto mb-4">
      <button
        onClick={() => navigate('/')}
        className="flex items-center text-sm text-gray-600 hover:text-black"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Home
      </button>
    </div>

    {/* CENTERED CARD */}
    <div className="flex justify-center">
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-sm p-8">

        {/* Header */}
        <h2 className="text-2xl font-semibold text-gray-800">
          Create Account
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Start your journey as a professional marketplace partner.
        </p>

        {/* Tabs */}
        <div className="flex bg-gray-100 rounded-lg p-1 w-fit mt-6">
          <button
            onClick={() => setFormData({ ...formData, role: "user" })}
            className={`px-4 py-1.5 text-sm rounded-md ${
              formData.role === "user"
                ? "bg-white shadow text-gray-800"
                : "text-gray-500"
            }`}
          >
            Buyer/Seller
          </button>
          <button
            onClick={() => setFormData({ ...formData, role: "agent" })}
            className={`px-4 py-1.5 text-sm rounded-md ${
              formData.role === "agent"
                ? "bg-white shadow text-gray-800"
                : "text-gray-500"
            }`}
          >
            Agent
          </button>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="mt-6 space-y-5">

          {/* Name + Email */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">Full Name</label>
              <input
                ref={emailRef}
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Email Address</label>
              <input
                type="email"
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                placeholder="Email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>
          </div>

          {/* Phone + City */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">Phone Number</label>
              <div className="flex mt-1 border rounded-md overflow-hidden">
                <span className="px-3 flex items-center text-gray-500 text-sm bg-gray-50">
                  +91
                </span>
                <input
                  className="w-full px-3 py-2 text-sm outline-none"
                  placeholder="98765 43210"
                  value={formData.phone}
                  maxLength={10}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      phone: e.target.value.replace(/\D/g, ""),
                    })
                  }
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-600">City</label>
              <select
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                value={formData.city}
                onChange={(e) =>
                  setFormData({ ...formData, city: e.target.value })
                }
              >
                <option value="">Select City</option>
                {cities.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Password */}
          <div className="relative">
            <label className="text-sm text-gray-600">Create Password</label>
            <input
              type={showPassword ? "text" : "password"}
              className="w-full mt-1 border rounded-md px-3 py-2 pr-10 text-sm"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-gray-500"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>

            <p className="text-xs text-gray-400 mt-1">
              Minimum 8 characters with a mix of letters and numbers
            </p>
          </div>

          {/* AGENT DOCUMENTS */}
          {formData.role === "agent" && (
            <div className="pt-4 border-t">
              <h3 className="font-semibold text-gray-800">
                Agent Documents
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Verification is required to list premium inventory.
              </p>

              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: "Business Registration", key: "business_registration" },
                  { label: "PAN Card", key: "pan_card" },
                  { label: "Aadhar Card", key: "aadhar_card" },
                ].map((doc) => (
                  <label
                    key={doc.key}
                    className="border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center text-center cursor-pointer hover:border-gray-400"
                  >
                    <FileText className="h-6 w-6 text-gray-500 mb-2" />
                    <span className="text-xs font-medium text-gray-700">
                      {doc.label}
                    </span>
                    <span className="text-[10px] text-gray-400">
                      PDF, JPG (Max 5MB)
                    </span>

                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, doc.key)}
                    />
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* TERMS */}
          <div className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            />
            <p className="text-gray-600">
              I agree to the{" "}
              <span
                onClick={() => setShowTermsDialog(true)}
                className="text-blue-600 cursor-pointer underline"
              >
                Terms of Service
              </span>{" "}
              and{" "}
              <span
                onClick={() => setShowTermsDialog(true)}
                className="text-blue-600 cursor-pointer underline"
              >
                Privacy Policy
              </span>
              {formData.role === "agent"
                ? " for Agents"
                : " for Buyers/Sellers"}
            </p>
          </div>

          {/* Button */}
          <button
            type="submit"
            disabled={!termsAccepted || loading}
            className="w-full bg-black text-white py-2.5 rounded-md hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Creating..." : "Create Account"}
          </button>

          <p className="text-center text-sm text-gray-500">
            Already have an account?{" "}
            <span
              className="text-blue-600 cursor-pointer"
              onClick={() => navigate("/login")}
            >
              Sign In
            </span>
          </p>
        </form>
      </div>
    </div>

    {/* TERMS DIALOG (unchanged) */}
    {showTermsDialog && (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white w-full max-w-2xl rounded-lg p-6 max-h-[80vh] overflow-y-auto">

          <h3 className="text-lg font-semibold mb-4">
            Terms and Conditions{" "}
            {formData.role === "agent"
              ? "for Agents"
              : "for Buyers/Sellers"}
          </h3>

          <div className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
            {formData.role === "agent" ? AGENT_TERMS : USER_TERMS}
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => setShowTermsDialog(false)}
              className="px-4 py-2 border rounded-md"
            >
              Close
            </button>

            <button
              onClick={() => {
                setTermsAccepted(true);
                setShowTermsDialog(false);
              }}
              className="px-4 py-2 bg-black text-white rounded-md"
            >
              I Accept
            </button>
          </div>

        </div>
      </div>
    )}
  </div>
);
};

export default Register;
